import React, { useCallback, useContext, useState } from 'react'
import Cropper from 'react-easy-crop'
import Slider from '@material-ui/core/Slider'
import uuid from 'react-uuid'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/atoms/button'
import { Area } from 'react-easy-crop/types'
import { AccountContext } from '@/contexts/account'
import { useFileUpload } from '@/hooks/use-file-upload'

type Props = {
  feature: string
  isPublic: boolean
  value: string
  title?: string
  onUpload: (uploadImageUrl: string) => void
  onClose: () => void
  aspect?: number
  cropShape?: 'round' | 'rect'
}

export const CropModal: React.FC<Props> = ({ isPublic, feature, value, title, onUpload, onClose, aspect = 1, cropShape = 'rect' }) => {
  const { t } = useTranslation()
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area>()
  const { account } = useContext<any>(AccountContext)
  const { upload: fileUpload } = useFileUpload()

  const onCropComplete = useCallback((croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels)
  }, [])

  const upload = async () => {
    if (!croppedAreaPixels) {
      return
    }
    const croppedImage: any = await getCroppedImg(value, croppedAreaPixels)
    const { url: uploadedPath } = await fileUpload({
      hotelId: account.employee.hotelId,
      file: {
        name: `${uuid()}.jpeg`,
        type: 'image/jpeg',
      },
      feature,
      fileData: croppedImage,
      isPublic,
    })
    onUpload(uploadedPath)
    onClose()
  }

  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image()
      image.addEventListener('load', () => resolve(image))
      image.addEventListener('error', error => reject(error))
      image.setAttribute('crossOrigin', 'anonymous')
      image.src = url
    })

  const getCroppedImg = async (imageSrc: string, pixelCrop: Area) => {
    const image = await createImage(imageSrc)
    const canvas = document.createElement('canvas')
    canvas.width = pixelCrop.width
    canvas.height = pixelCrop.height
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height)
    return canvas.toDataURL('image/jpeg')
  }

  return (
    <>
      <div css={backgroundStyle}></div>
      <div css={cropModalContainerStyle}>
        <div css={cropModalMainContainerStyle}>
          <div className="crop-container" css={cropShape === 'round' ? cropperInsideRoundStyle : cropperProductPictureInsideStyle}>
            <div css={cropModalHeaderStyle}>{title ?? t('Set image')}</div>
            <div css={cropWrapperStyle}>
              <div css={cropTopSpaceStyle} />
              <div css={cropperStyle}>
                <Cropper
                  image={value}
                  crop={crop}
                  zoom={zoom}
                  aspect={aspect}
                  cropShape={cropShape}
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                />
              </div>
              <div css={cropBottomSpaceStyle} />
            </div>
            <div css={cropModalFooterStyle}>
              <div css={cropModalSliderContainerStyle}>
                <img src={require('@/static/images/picture.svg')} css={sliderIconSmStyle} />
                <Slider value={zoom} min={1} max={3} step={0.1} aria-labelledby="Zoom" onChange={(e, zoom: any) => setZoom(zoom)} />
                <img src={require('@/static/images/picture.svg')} css={sliderIconLgStyle} />
              </div>
              <div css={cropModalButtonContainerStyle}>
                <Button buttonType={3} width={110} height={38} marginLeft={16} onClick={() => onClose()}>
                  {t('Cancel')}
                </Button>
                <Button buttonType={1} width={110} height={38} marginLeft={16} onClick={() => upload()}>
                  {t('Apply')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

// cropModal
// flex コンテナ配下だと z-index 指定がある時点で stacking context を作るため、
// 中身(cropModalMainContainerStyle)の重なり順は親のこの値に制限される。overlay より前面の値にすること
const cropModalContainerStyle = css({
  zIndex: 105,
  visibility: 'visible',
})

const cropModalMainContainerStyle = css({
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  '-webkit-transform': 'translate(-50%, -50%)',
  '-ms-transform': 'translate(-50%, -50%)',
  width: 600,
  height: 450,
  background: 'white',
  borderRadius: '5px',
  zIndex: 105,
})

const cropperProductPictureInsideStyle = css({
  height: '100%',
  position: 'relative',
  '.reactEasyCrop': {
    '&_CropArea': {
      boxShadow: '0 0 0 9999em rgba(0,0,0,0.1)',
      border: '2px solid #E7A73C',
      ':before': {
        border: 'none',
      },
      ':after': {
        border: 'none',
      },
    },
  },
})

const cropperInsideRoundStyle = css(cropperProductPictureInsideStyle, {
  '.reactEasyCrop': {
    '&_CropArea': {
      borderRadius: '50%',
    },
  },
})

const cropModalHeaderStyle = css({
  zIndex: 1,
  background: '#FFF',
  width: '100%',
  height: 50,
  padding: '16px 0px 16px 32px',
  fontSize: '18px',
  color: '#272727',
  fontWeight: 'bold',
  borderRadius: '5px 5px 0 0',
  boxShadow: '0px 1px 6px rgba(0,0,0,0.10)',
})

const cropModalFooterStyle = css({
  position: 'absolute',
  bottom: 0,
  display: 'flex',
  padding: '14px 32px',
  width: '100%',
  justifyContent: 'space-between',
  background: '#FFF',
  borderRadius: '0 0 5px 5px',
})

const cropModalSliderContainerStyle = css({
  display: 'flex',
  alignItems: 'center',
  width: 210,
  '.MuiSlider': {
    '&-rail': {
      height: 1,
      color: '#CCC',
    },
    '&-root': {
      color: '#E7A73C',
    },
    '&-thumb': {
      color: '#FFF',
      border: '2px solid #E7A73C',
      borderRadius: '50%',
      ':hover': {
        // #E7A73C
        boxShadow: '0px 0px 0px 8px rgba(231, 167, 60, 0.2)',
      },
    },
  },
})

const cropWrapperStyle = css({
  width: 600,
  height: 340,
  position: 'relative',
  padding: '40px 0',
})

const cropTopSpaceStyle = css({
  width: '100%',
  height: 40,
  backgroundColor: '#e5e5e5',
  top: '0px',
  position: 'absolute',
})

const cropBottomSpaceStyle = css({
  width: '100%',
  height: 40,
  backgroundColor: '#e5e5e5',
  bottom: '0px',
  position: 'absolute',
})

const cropperStyle = css({
  position: 'absolute',
  top: '40px',
  left: 0,
  right: 0,
  bottom: '40px',
  height: 260,
})

const sliderIconSmStyle = css({
  width: 20,
  paddingRight: 8,
})

const sliderIconLgStyle = css({
  paddingLeft: 8,
})

const cropModalButtonContainerStyle = css({
  display: 'flex',
})

// 画面側に position 指定の要素（InputField など）があるとオーバーレイより前面に描画されるため、重なり順を明示する。
// ヘッダー(102)・サイドメニュー(103)もクロップ中は操作させないため、それらより前面に出す
const backgroundStyle = css({
  position: 'fixed',
  width: '100%',
  height: '100%',
  backgroundColor: '#000000',
  opacity: 0.5,
  top: 0,
  left: 0,
  zIndex: 104,
})
